// Stage-chip multi-select state. Kept in a dedicated Zustand store so that:
//  (a) the chips component can subscribe with a shallow selector and only
//      re-render on relevant transitions;
//  (b) the members-panel widget reads the same selection without prop
//      drilling — the chips and the list are siblings;
//  (c) navigating away from the page resets the selection (the page calls
//      `reset` on unmount), preventing chip state from outliving the surface.
//
// In-memory only — no persist middleware. The natural Mini App lifecycle
// (open → swipe-away → reopen) implies the user wants a fresh view, not a
// cached chip selection from yesterday.

import { create } from "zustand";

import {
  MEMBER_LIFECYCLE_STAGES,
  type MemberLifecycleStage,
} from "@community-analytics/shared";

export interface FilterMembersState {
  /** Empty array = "all stages". Sorted in the canonical PRD order. */
  selectedStages: ReadonlyArray<MemberLifecycleStage>;
}

export interface FilterMembersActions {
  toggleStage: (stage: MemberLifecycleStage) => void;
  setStages: (stages: ReadonlyArray<MemberLifecycleStage>) => void;
  clear: () => void;
  reset: () => void;
}

export type FilterMembersStore = FilterMembersState & FilterMembersActions;

export const INITIAL_FILTER_MEMBERS_STATE: FilterMembersState = {
  selectedStages: [],
};

const STAGE_ORDER = new Map<MemberLifecycleStage, number>(
  MEMBER_LIFECYCLE_STAGES.map((s, i) => [s, i] as const),
);

function withCanonicalOrder(
  stages: ReadonlyArray<MemberLifecycleStage>,
): ReadonlyArray<MemberLifecycleStage> {
  const seen = new Set<MemberLifecycleStage>();
  const filtered = stages.filter((s) => {
    if (seen.has(s)) return false;
    seen.add(s);
    return true;
  });
  return [...filtered].sort(
    (a, b) => (STAGE_ORDER.get(a) ?? 0) - (STAGE_ORDER.get(b) ?? 0),
  );
}

export const useFilterMembersStore = create<FilterMembersStore>((set) => ({
  ...INITIAL_FILTER_MEMBERS_STATE,
  toggleStage: (stage) =>
    set((s) => {
      const next = s.selectedStages.includes(stage)
        ? s.selectedStages.filter((x) => x !== stage)
        : [...s.selectedStages, stage];
      return { selectedStages: withCanonicalOrder(next) };
    }),
  setStages: (stages) => set({ selectedStages: withCanonicalOrder(stages) }),
  clear: () => set({ selectedStages: [] }),
  reset: () => set(INITIAL_FILTER_MEMBERS_STATE),
}));

export const selectSelectedStages = (
  s: FilterMembersStore,
): ReadonlyArray<MemberLifecycleStage> => s.selectedStages;

export const isStageSelected =
  (stage: MemberLifecycleStage) =>
  (s: FilterMembersStore): boolean =>
    s.selectedStages.includes(stage);
